import { Injectable, signal, computed } from '@angular/core';
import { MamAsset, MamStatus, MamType } from '../../domain/mam-explorer';

const DEFAULT_PAGE_SIZE = 20;

@Injectable({ providedIn: 'root' })
export class MamExplorerStateService {
  // ── Writable signals ──────────────────────────────────────

  private readonly assetsSignal = signal<MamAsset[]>([]);
  private readonly totalCountSignal = signal<number>(0);
  private readonly readyCountSignal = signal<number>(0);
  private readonly processingCountSignal = signal<number>(0);

  private readonly searchQuerySignal = signal<string>('');
  private readonly statusFilterSignal = signal<MamStatus | 'All'>('All');
  private readonly typeFilterSignal = signal<MamType | 'All'>('All');

  private readonly selectedIdsSignal = signal<Set<number>>(new Set());
  private readonly pageIndexSignal = signal<number>(0);
  private readonly pageSizeSignal = signal<number>(DEFAULT_PAGE_SIZE);

  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);

  // ── Readonly public signals ───────────────────────────────

  readonly assets = this.assetsSignal.asReadonly();
  readonly totalCount = this.totalCountSignal.asReadonly();
  readonly readyCount = this.readyCountSignal.asReadonly();
  readonly processingCount = this.processingCountSignal.asReadonly();

  readonly searchQuery = this.searchQuerySignal.asReadonly();
  readonly statusFilter = this.statusFilterSignal.asReadonly();
  readonly typeFilter = this.typeFilterSignal.asReadonly();

  readonly selectedIds = this.selectedIdsSignal.asReadonly();
  readonly pageIndex = this.pageIndexSignal.asReadonly();
  readonly pageSize = this.pageSizeSignal.asReadonly();

  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();

  // ── Computed signals ──────────────────────────────────────

  readonly hasSelection = computed(() => this.selectedIdsSignal().size > 0);
  readonly selectedCount = computed(() => this.selectedIdsSignal().size);

  // ── Setters ───────────────────────────────────────────────

  setAssets(assets: MamAsset[]): void {
    this.assetsSignal.set(assets);
  }

  setTotalCount(n: number): void {
    this.totalCountSignal.set(n);
  }

  setReadyCount(n: number): void {
    this.readyCountSignal.set(n);
  }

  setProcessingCount(n: number): void {
    this.processingCountSignal.set(n);
  }

  setSearchQuery(q: string): void {
    this.searchQuerySignal.set(q);
  }

  setStatusFilter(s: MamStatus | 'All'): void {
    this.statusFilterSignal.set(s);
  }

  setTypeFilter(t: MamType | 'All'): void {
    this.typeFilterSignal.set(t);
  }

  setSelectedIds(ids: Set<number>): void {
    this.selectedIdsSignal.set(new Set(ids));
  }

  toggleSelection(id: number): void {
    const current = new Set(this.selectedIdsSignal());
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    this.selectedIdsSignal.set(current);
  }

  selectAll(ids: number[]): void {
    this.selectedIdsSignal.set(new Set(ids));
  }

  clearSelection(): void {
    this.selectedIdsSignal.set(new Set());
  }

  setPageIndex(i: number): void {
    this.pageIndexSignal.set(i);
  }

  setPageSize(s: number): void {
    this.pageSizeSignal.set(s);
  }

  setLoading(b: boolean): void {
    this.loadingSignal.set(b);
  }

  setError(e: string | null): void {
    this.errorSignal.set(e);
  }

  reset(): void {
    this.assetsSignal.set([]);
    this.totalCountSignal.set(0);
    this.readyCountSignal.set(0);
    this.processingCountSignal.set(0);
    this.searchQuerySignal.set('');
    this.statusFilterSignal.set('All');
    this.typeFilterSignal.set('All');
    this.selectedIdsSignal.set(new Set());
    this.pageIndexSignal.set(0);
    this.pageSizeSignal.set(DEFAULT_PAGE_SIZE);
    this.loadingSignal.set(false);
    this.errorSignal.set(null);
  }
}
